import * as Sentry from '@sentry/react';
import { Box, Flex } from '@televet/kibble-ui';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { Icon } from '@televet/kibble-ui/build/components/Icon';
import { Tag } from '@televet/kibble-ui/build/components/Tag';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import React, { ComponentProps } from 'react';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import { GetAppointmentAiRecapInfoQuery, RecapStatus, useGetAppointmentRecapUrlLazyQuery } from 'shared/types/graphql';
import { audioStringToMp3Blob, triggerDownloadFromBlob } from 'shared/utils/fileDownloader';
import { formatToSimpleDate } from 'shared/utils/formatDate';
import { formatDuration } from 'shared/utils/formatTime';
import AudioPlayerButton from './AudioPlayerButton';

const recapTagVariantMap: Record<RecapStatus, ComponentProps<typeof Tag>['variant']> = {
  [RecapStatus.Created]: 'warning',
  [RecapStatus.Complete]: 'success',
  [RecapStatus.Error]: 'error',
  [RecapStatus.GeneratingSummary]: 'warning',
  [RecapStatus.Transcribing]: 'warning',
  [RecapStatus.Uploading]: 'warning',
};

const recapTagLabelMap: Record<RecapStatus, string> = {
  [RecapStatus.Created]: 'Created',
  [RecapStatus.Complete]: 'Completed',
  [RecapStatus.Error]: 'Error',
  [RecapStatus.GeneratingSummary]: 'Processing',
  [RecapStatus.Transcribing]: 'Processing',
  [RecapStatus.Uploading]: 'Processing',
};

type RecordingItemProps = {
  recap: GetAppointmentAiRecapInfoQuery['findManyRecap'][number];
  index: number;
  total: number;
};

const RecordingItem = ({ recap, index, total }: RecordingItemProps): JSX.Element => {
  const isLastElement = index === total - 1;
  const toast = useToast();
  const [getUrl, { loading }] = useGetAppointmentRecapUrlLazyQuery();

  const handleDownload = async (): Promise<void> => {
    try {
      const recapData = await getUrl({
        variables: { data: { recapId: recap.id } },
        fetchPolicy: GraphQLFetchPolicies.CacheFirst,
      });
      const mp3Base64 = recapData?.data?.getAppointmentRecapUrl;

      if (!mp3Base64) {
        throw new Error('No audio data found for recapId: ' + recap.id);
      }

      const blob = audioStringToMp3Blob(mp3Base64);
      triggerDownloadFromBlob(blob, `Recording_${total - index}.mp3`);
    } catch (e) {
      Sentry.captureException(e);
      toast({
        title: 'Error downloading audio',
        description: 'We could not download the audio file. Please try again later.',
        status: 'error',
      });
    }
  };

  return (
    <Box
      pt={4}
      {...(!isLastElement && {
        borderBottom: '1px',
        borderColor: 'border.default',
      })}
    >
      <Flex justifyContent="space-between">
        <Flex direction="column">
          <Text size="md" as="p" mb={2}>
            Recording #{total - index}
          </Text>
          <Box>
            <Text size="sm" as="p" mb={1} color="text.subtle">
              {!!recap.durationSeconds ? formatDuration(recap.durationSeconds) : '00:00'}
            </Text>
            <Flex gap={1}>
              <Icon name="person" size="sm" />
              <Text size="sm" as="p" color="text.subtle">
                {recap.createdBy?.firstName} {recap.createdBy?.lastName}
              </Text>
            </Flex>
          </Box>
        </Flex>
        <Flex direction="column" width="80px" justifyContent="space-between" alignItems="flex-end">
          <Text size="sm">{formatToSimpleDate(recap.createdAt)}</Text>
          <Tag variant={recapTagVariantMap[recap.status]} label={recapTagLabelMap[recap.status]} size="sm" />
        </Flex>
      </Flex>
      {recap.status === RecapStatus.Complete && recap.recordingS3Uri && (
        <Box mt={3}>
          <AudioPlayerButton recapId={recap.id} />
          <Button
            size="sm"
            iconName="download"
            isFullWidth
            variant="ghostNeutral"
            onClick={handleDownload}
            isLoading={loading}
          >
            Download .mp3
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default RecordingItem;
